import React, { ReactNode, useEffect } from 'react';
import Lottie from 'lottie-react';
import helloCharacterOnly from '@assets/illustration/Hello - Character Only.json';
import { atom, useAtom } from 'jotai';
import { isTeacherChatReadyAtom } from './FloatingButton';
import {
  clickFloatingButtonAtom,
  resetDiscussQuestionAtom,
  showGenieAskFloatingButtonAtom,
} from '@atom/worksheetAtom';
import { Container } from 'geniebook-ui/dist';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useParams } from 'react-router-dom';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';

export const minichatOpenAtom = atom(false);
interface MinichatFloatingButtonWrapperProps {
  children: ReactNode;
  bottomPosition?: string;
}

interface BreakpointPosition {
  width: number;
  nonJC: string;
  JC: string;
}

const POSITION_BREAKPOINTS: BreakpointPosition[] = [
  {
    width: 1476,
    nonJC: '-right-[138px]',
    JC: 'right-0',
  },
  {
    width: 1366,
    nonJC: '-right-[83px]',
    JC: 'right-[30px]',
  },
  {
    width: 1024,
    nonJC: 'right-0',
    JC: 'right-[150px]',
  },
];

const MinichatFloatingButtonWrapper: React.FC<
  MinichatFloatingButtonWrapperProps
> = ({ children, bottomPosition }) => {
  const [rightClass, setRightClass] = React.useState('');
  const width = useViewWidth();
  const { subjectId: subjectName } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(subjectName ?? ''),
  });

  const getRightClass = (width: number): string => {
    const breakpoint = POSITION_BREAKPOINTS.find(bp => width >= bp.width);
    return breakpoint
      ? isJC === false
        ? breakpoint.nonJC
        : breakpoint.JC
      : '';
  };

  useEffect(() => {
    const classForWidth = getRightClass(width);
    setRightClass(classForWidth);
  }, [width]);

  const [teacherChatReady] = useAtom(isTeacherChatReadyAtom);
  const [clickFloatingButton, setClickFloatingButton] = useAtom(
    clickFloatingButtonAtom,
  );
  const [minichatShown, setMinichatShown] = useAtom(minichatOpenAtom);
  const [, setResetDiscussQuestionProps] = useAtom(resetDiscussQuestionAtom);
  const [showGenieAskFloatingButton] = useAtom(showGenieAskFloatingButtonAtom);
  const handleClickMinichatAnimation = () => {
    setResetDiscussQuestionProps(true);
    setClickFloatingButton(!clickFloatingButton);
  };

  // observe the DOM to detect if popover minichat is exist or not. based on that `z-20` style will be set to the floating button wrapper
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        const popperContentWrapper = document.querySelector(
          '[data-radix-popper-content-wrapper]',
        );
        popperContentWrapper ? setMinichatShown(true) : setMinichatShown(false);
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return (
    <>
      {showGenieAskFloatingButton && rightClass && (
        <Container className={'fixed bottom-0 z-20'}>
          <div className={'relative w-full'}>
            <div
              id="minichat_floating_button_wrapper"
              className={`${rightClass} absolute ${
                minichatShown ? '' : 'z-20'
              } mr-[30px] flex ${
                teacherChatReady ? 'cursor-pointer' : 'cursor-default'
              } flex-col max-lg:hidden max-md:right-0 ${
                bottomPosition ??
                'lg:bottom-0 max-lg:bottom-[50px] max-md:bottom-[70px]'
              }`}
              data-testid="floating-button-wrapper"
            >
              {isJC === false && (
                <Lottie
                  id="astronaut_hello"
                  animationData={helloCharacterOnly}
                  className={`m-auto w-[120px] h-[120px] ${
                    minichatShown ? 'invisible' : ''
                  }`}
                  onClick={() => handleClickMinichatAnimation()}
                />
              )}
              {children}
            </div>
          </div>
        </Container>
      )}
    </>
  );
};

export default MinichatFloatingButtonWrapper;
